import type {
  OpportunityOutcome,
  OpportunityStageType,
} from '@/services/bff/types/opportunity';

/**
 * 商机结局（进行中/赢单/输单）由所属商机阶段派生，不再读取 crm_opportunity.end_status。
 * 本文件是 server 端 shared/opportunity-status.util.ts 的镜像实现：
 * 两个仓库是独立的 pnpm workspace，没有共享包，只能各写一份，改动时需同步。
 */
export interface OpportunityStageOutcomeFields {
  stageType?: OpportunityStageType | string | null;
  isWon?: number | boolean | string | null;
  isLost?: number | boolean | string | null;
}

const isFlagSet = (value: number | boolean | string | null | undefined) => {
  if (value === true) return true;
  if (value === false || value === null || value === undefined) return false;
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0;
};

/**
 * 数值标志优先于 stageType：stage_type 是 NOT NULL DEFAULT 'normal'，
 * 早于该列存在的行会是 'normal' 而 isWon 为 1。
 * 无阶段或阶段已软删视为进行中。
 */
export const resolveOpportunityOutcomeFromStage = (
  stage?: OpportunityStageOutcomeFields | null,
): OpportunityOutcome => {
  if (!stage) return 'ongoing';
  if (isFlagSet(stage.isWon)) return 'won';
  if (isFlagSet(stage.isLost)) return 'lost';
  const stageType = String(stage.stageType || '')
    .trim()
    .toLowerCase();
  if (stageType === 'won') return 'won';
  if (stageType === 'lost') return 'lost';
  return 'ongoing';
};

/** 商机结局的中文标签；进行中返回空串，便于调用方决定是否渲染。 */
export const resolveOpportunityOutcomeLabel = (
  outcome?: OpportunityOutcome,
) => {
  if (outcome === 'on_hold') return '已搁置';
  if (outcome === 'closed') return '已关闭（未成交）';
  if (outcome === 'won') return '赢单';
  if (outcome === 'lost') return '输单';
  return '';
};

/** 与列表筛选、Tag 配色共用的配色约定。 */
export const resolveOpportunityOutcomeColor = (
  outcome?: OpportunityOutcome,
) => {
  if (outcome === 'on_hold') return 'orange';
  if (outcome === 'closed') return 'default';
  if (outcome === 'won') return 'green';
  if (outcome === 'lost') return 'red';
  return 'default';
};

export const resolveOpportunityOutcome = (
  opportunity?: { lifecycleStatus?: string | null } | null,
  stage?: OpportunityStageOutcomeFields | null,
): OpportunityOutcome => {
  if (opportunity?.lifecycleStatus === 'on_hold') return 'on_hold';
  if (opportunity?.lifecycleStatus === 'closed') return 'closed';
  return resolveOpportunityOutcomeFromStage(stage);
};
